import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { Modal } from 'reactstrap';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import { setCurrentModal } from '../thunks/setCurrentModal';
import ModalHeader from '../components/ModalHeader';
import StandardButton from '../components/StandardButton';
import { OrderingSelectors, OrderingOperations, OrderingConstants } from 'polygon-ordering';
import { addToRecentLocations, clearRecentLocations } from '../slices/recentLocations';
import { applyBufferDeliveryEstimate } from '../thunks/applyBufferDeliveryEstimate';
import { confirmLocationEstimate } from '../thunks/confirmLocationEstimate';
import { performDeliveryEstimate } from '../thunks/performDeliveryEstimate';
import { adjustOrder } from '../thunks/adjustOrder';
import { getEnableMultipleDeliveryEstimate } from '../libs/polygon-ordering/src/selectors/config';
import { updateLoadingDependencies } from '../slices/loadingDependencies';
import combineStyles from '../utils/combineStyles';
import { setDesiredDeliveryAddress } from '../slices/desiredDeliveryAddress';
import { DeliveryTimeAndAddressSelection } from '../components/DeliveryTimeAndAddressSelection';
import { AddressLookupAndConfirmation } from '../components/AddressLookupAndConfirmation';
import { DeliveryDetailsConfirmation } from '../components/DeliveryDetailsConfirmation';
import { saveAddress } from '../libs/polygon-ordering/src/operations';
import { resetForm, setSelectedAddress, updateForm } from '../slices/deliveryAddressForm';
import { setInlineAlert } from '../slices/inlineAlert';
import {
  BackButtonParams,
  ContinueButtonDisabledParams,
  ContinueButtonParams,
  ContinueButtonTextParams,
  DeliveryDetailsModalAction,
  getBackButtonActionPlan,
  getContinueButtonActionPlan,
  getContinueButtonText,
  getModalTitleText,
  isContinueButtonDisabled,
  isContinueButtonLoading,
} from './DeliveryDetailsModal.logic';
import {
  getDeliveryAddressFromPlaceIdAsync,
  getGeocodingDetailsAsync,
} from '../libs/polygon-utils/src/GoogleMapsApiHandler';
import poweredByGoogleSvg from '../images/powdered-by-google.svg';
import { setBufferDeliveryTime } from '../libs/polygon-ordering/src/reducers/buffer/deliveryTime';
import { saleTypeSelected } from '../utils/analytics';

export const DELIVERY_DETAILS_MODAL_ID = 'DELIVERY_DETAILS_MODAL_ID';
const { SALE_TYPE } = OrderingConstants;
const {
  getBufferDeliveryTime,
  getDeliveryReadyToApply,
  getDeliveryRequiresLocationSelection,
  getKeyOrderPropertyUpdateInProgress,
  getLocationDeliveryEstimates,
  getLocationsForDisplay,
  getLocation,
} = OrderingSelectors;

const { setDeliveryNotes, setDeliveryDetails } = OrderingOperations;

const DeliveryDetails = () => {
  const dispatch = useAppDispatch();
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const keyOrderPropertyUpdateInProgress = useAppSelector(getKeyOrderPropertyUpdateInProgress);
  const bufferDeliveryTime = useAppSelector(getBufferDeliveryTime);
  const desiredDeliveryTime = useAppSelector(state => state.desiredDeliveryTime);
  const effectiveDeliveryAddress = useAppSelector(state => state.desiredDeliveryAddress);
  const effectiveDeliveryTime = desiredDeliveryTime || bufferDeliveryTime;
  const deliveryReadyToApply = useAppSelector(getDeliveryReadyToApply);
  const deliveryRequiresLocationSelection = useAppSelector(getDeliveryRequiresLocationSelection);
  const locations = useAppSelector(getLocationsForDisplay);
  const locationDeliveryEstimates = useAppSelector(getLocationDeliveryEstimates);
  const enableMultipleDeliveryEstimate = useAppSelector(getEnableMultipleDeliveryEstimate);
  const deliveryEstimateFailed = useAppSelector(state => state.deliveryEstimateFailed);
  const googleMapsApiKey = useAppSelector(state => state.config.googleMapsApiKey);
  const modalParams = useAppSelector(state => state.modalParams);
  const formState = useAppSelector(state => state.deliveryAddressForm);
  const saleType = modalParams?.saleType || SALE_TYPE.DELIVERY;
  const [selectedLocationId, setSelectedLocationId] = useState<null | string>(null);
  const recentAddresses = useAppSelector(state => state.recentLocations);
  const deliveryTimeSelected = !!useAppSelector(getBufferDeliveryTime);
  const location = useAppSelector(getLocation);

  useEffect(() => {
    const singleDeliverySingleLocation =
      !enableMultipleDeliveryEstimate && locationDeliveryEstimates.length;
    const multiDeliverySingleLocation =
      enableMultipleDeliveryEstimate && locationDeliveryEstimates.length === 1;
    if (singleDeliverySingleLocation || multiDeliverySingleLocation) {
      const locationId = locations[locationDeliveryEstimates[0]?.locationId]?.id;
      if (locationId) {
        setSelectedLocationId(locationId);
      }
    }
  }, [locationDeliveryEstimates, enableMultipleDeliveryEstimate]);

  useEffect(() => {
    if (!effectiveDeliveryAddress || !effectiveDeliveryTime) {
      dispatch(setInlineAlert({ visible: false }));
      return;
    }
    dispatch(
      performDeliveryEstimate({
        deliveryAddress: effectiveDeliveryAddress,
        desiredDeliveryTime: effectiveDeliveryTime,
        saleType,
      }),
    );
  }, [effectiveDeliveryAddress, effectiveDeliveryTime]);

  const processActions = async (actions: DeliveryDetailsModalAction[]) => {
    for (const action of actions) {
      switch (action.type) {
        case 'resetForm':
          dispatch(resetForm());
          break;
        case 'closeModal':
          dispatch(setCurrentModal(null));
          break;
        case 'applyBufferDeliveryEstimate':
          dispatch(applyBufferDeliveryEstimate());
          break;
        case 'clearRecentLocations':
          dispatch(clearRecentLocations());
          break;
      }

      if (action.payload === undefined) {
        continue;
      }

      switch (action.type) {
        case 'updateForm':
          dispatch(updateForm(action.payload));
          break;
        case 'adjustOrder':
          // Log analytics when delivery flow is complete
          saleTypeSelected({ saleType, store: location?.name });
          await dispatch(adjustOrder(action.payload));
          break;
        case 'confirmLocationEstimate':
          dispatch(confirmLocationEstimate(action.payload));
          break;
        case 'setDeliveryNotes':
          dispatch(setDeliveryNotes(action.payload));
          break;
        case 'setDeliveryDetails':
          dispatch(setDeliveryDetails(action.payload));
          break;
        case 'setDesiredDeliveryAddress':
          dispatch(setDesiredDeliveryAddress(action.payload));
          break;
        case 'saveAddress':
          await dispatch(saveAddress(action.payload));
          break;
        case 'addToRecentLocations':
          dispatch(addToRecentLocations(action.payload));
          break;
        case 'setSelectedAddress':
          dispatch(setSelectedAddress(action.payload));
          break;
        case 'setInlineAlert':
          dispatch(setInlineAlert(action.payload));
          break;
        case 'setBufferDeliveryTime':
          dispatch(setBufferDeliveryTime(action.payload));
          break;
      }
    }
  };

  const onBackButtonClicked = async () => {
    dispatch(updateLoadingDependencies({ handleURLParamsInProgress: false }));

    const backButtonParams: BackButtonParams = {
      page: formState.page,
      addressSelectionView: formState.addressSelectionView,
      editingAddress: formState.editingAddress,
      adjustingPin: formState.adjustingPin,
      addressInput: formState.addressInput,
    };

    const actions = getBackButtonActionPlan(backButtonParams);

    await processActions(actions);
  };

  const onContinueButtonClicked = async () => {
    dispatch(updateLoadingDependencies({ handleURLParamsInProgress: false }));

    const params: ContinueButtonParams = {
      getDeliveryAddressFromPlaceIdAsyncFn: getDeliveryAddressFromPlaceIdAsync,
      getGeocodingDetailsAsyncFn: getGeocodingDetailsAsync,
      page: formState.page,
      deliveryReadyToApply,
      deliveryRequiresLocationSelection,
      selectedLocationId,
      saleType,
      effectiveDeliveryTime,
      effectiveDeliveryAddress,
      addressSelectionView: formState.addressSelectionView,
      editingAddress: formState.editingAddress,
      selectedAddress: formState.selectedAddress,
      googleMapsApiKey,
      adjustingPin: formState.adjustingPin,
      pinCoordinates: formState.pinCoordinates,
      recentAddresses,
    };

    const preActions = await getContinueButtonActionPlan(params, 'preActions');
    await processActions(preActions);

    const actions = await getContinueButtonActionPlan(params);
    await processActions(actions);
  };

  const onToggleModal = () => {
    dispatch(setCurrentModal(null));
    dispatch(resetForm());
    dispatch(updateLoadingDependencies({ handleURLParamsInProgress: false }));
  };

  // We are storing the modal content height in the form state so that the
  // Google Map component can adjust it's height accordingly. The Map requires
  // an explicit height to render at all, so we want to ensure the height of
  // the map is relative to the modal viewport.
  const modalContentRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const updateHeight = () => {
      if (modalContentRef.current) {
        dispatch(updateForm({ modalHeight: modalContentRef.current.clientHeight }));
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [formState.page]);

  const continueButtonDisabledParams: ContinueButtonDisabledParams = {
    keyOrderPropertyUpdateInProgress,
    submittingForm: formState.submittingForm,
    page: formState.page,
    effectiveDeliveryAddress,
    deliveryEstimateFailed,
    deliveryRequiresLocationSelection,
    selectedLocationId,
    adjustingPin: formState.adjustingPin,
    selectedAddress: formState.selectedAddress,
    addressSelectionView: formState.addressSelectionView,
    deliveryTimeSelected,
    saleType,
  };

  const continueButtonTextParams: ContinueButtonTextParams = {
    page: formState.page,
    addressSelectionView: formState.addressSelectionView,
    adjustingPin: formState.adjustingPin,
    editingAddress: formState.editingAddress,
    selectedAddress: formState.selectedAddress,
  };

  return (
    <Modal
      isOpen={true}
      scrollable
      fullscreen={deviceTypeMobile ? true : false}
      toggle={onToggleModal}
      fade={true}
      style={styles.modal}
    >
      <div style={styles.modalHeaderContainer}>
        <ModalHeader backClick={onBackButtonClicked} title={getModalTitleText(formState)} />
      </div>

      <div ref={modalContentRef} style={styles.modalContentContainer}>
        {formState.page === 'DeliveryTimeAndAddressSelection' && (
          <DeliveryTimeAndAddressSelection
            saleType={saleType}
            selectedLocationId={selectedLocationId}
            setSelectedLocationId={setSelectedLocationId}
          />
        )}
        {formState.page === 'AddressLookupAndConfirmation' && <AddressLookupAndConfirmation />}
        {formState.page === 'DeliveryDetailsConfirmation' && <DeliveryDetailsConfirmation />}
      </div>

      {formState.page === 'AddressLookupAndConfirmation' &&
        formState.addressSelectionView === 'Lookup' && (
          <div style={styles.poweredByGoogleContainer}>
            <img src={poweredByGoogleSvg} alt="Powered by Google" />
          </div>
        )}

      <div
        style={combineStyles(
          styles.modalFooterContainer,
          deviceTypeMobile && styles.modalFooterContainerMobile,
        )}
      >
        <StandardButton
          label={getContinueButtonText(continueButtonTextParams)}
          disabled={isContinueButtonDisabled(continueButtonDisabledParams)}
          themeKey="continueButton"
          onClick={onContinueButtonClicked}
          showSpinner={isContinueButtonLoading(continueButtonDisabledParams)}
          style={styles.continueButton}
        />
      </div>
    </Modal>
  );
};

const styles: Styles = {
  modal: {
    maxWidth: 660,
  },
  modalContentContainer: {
    overflow: 'auto',
    height: '100vh',
  },
  modalFooterContainer: {
    bottom: 0,
    width: '100%',
    padding: '20px 50px',
    borderTop: '1px solid #e0e0e0',
  },
  modalFooterContainerMobile: {
    padding: '30px',
  },
  poweredByGoogleContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 16,
    paddingBottom: 16,
  },
  continueButton: {
    maxWidth: 'none',
  },
};

export default DeliveryDetails;
